import { create } from 'zustand'
import { resetSignalProviderListFilters } from '@/stores/useSignalProviderListFiltersStore'

export type Role =
  | 'PLATFORM_ADMIN'
  | 'ADMIN'
  | 'EDITOR'
  | 'VIEWER'

interface User {
  id: string
  email: string
  name: string
  role: Role
  tenantId: string | null
  /** Tenant DNS label from identity `/me`; null for platform-only users. */
  subdomain: string | null
  /** Platform admin must change seeded password before other routes (BFP-517). */
  requiresPasswordReset?: boolean
}

interface SessionState {
  user: User | null
  isAuthenticated: boolean
  /**
   * Set by `clearSession()` (logout, /me error). When true, guards treat the user as
   * signed out immediately — do not wait on a refetching `/me` (avoids re-sessioning
   * while cookies are still clearing).
   */
  explicitLogout: boolean
  /**
   * True while login/logout is establishing or tearing down cookies.
   * Blocks `/me` until the active auth mutation finishes so parallel
   * requests never hit identity without cookies.
   */
  authTransitionInProgress: boolean
  sidebarOpen: boolean
  setUser: (user: User | null) => void
  clearSession: () => void
  setAuthTransitionInProgress: (inProgress: boolean) => void
  toggleSidebar: () => void
}

export const useSessionStore = create<SessionState>((set) => ({
  user: null,
  isAuthenticated: false,
  explicitLogout: false,
  authTransitionInProgress: false,
  sidebarOpen: true,
  setUser: (user) =>
    set({
      user: user
        ? { ...user, requiresPasswordReset: user.requiresPasswordReset ?? false }
        : null,
      isAuthenticated: !!user,
      explicitLogout: false,
    }),
  clearSession: () => {
    resetSignalProviderListFilters()
    set({
      user: null,
      isAuthenticated: false,
      explicitLogout: true,
      authTransitionInProgress: false,
    })
  },
  setAuthTransitionInProgress: (inProgress) =>
    set({ authTransitionInProgress: inProgress }),
  toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
}))
